import React, { useCallback } from 'react';
import { Stack } from '@cardboard-ui/react';
import { CardContent } from 'components/cards/Card';
import debounce from 'lodash/debounce';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { QuestionControls } from './QuestionControls';
import { EditQuestionCard_formQuestion$key } from './__generated__/EditQuestionCard_formQuestion.graphql';
import {
  EditQuestionCard_updateQuestion_Mutation,
  FormsUpdateQuestionMutationInput,
} from './__generated__/EditQuestionCard_updateQuestion_Mutation.graphql';
import { QuestionInputs } from './QuestionInputs';
import { QuestionTitle } from './QuestionTitle';
import {
  DraggableCard,
  DraggableCardProps,
} from 'components/cards/DraggableCard';

const fragment = graphql`
  fragment EditQuestionCard_formQuestion on FormQuestion {
    id
    ...QuestionTitle_question
    ...QuestionInputs_question
    ...QuestionControls_question
  }
`;

const updateMutation = graphql`
  mutation EditQuestionCard_updateQuestion_Mutation(
    $input: FormsUpdateQuestionMutationInput!
  ) {
    updateQuestion(input: $input) {
      question {
        ...EditQuestionCard_formQuestion
      }
    }
  }
`;

interface EditQuestionCardProps extends DraggableCardProps {
  question: EditQuestionCard_formQuestion$key;
  formId: string;
  isDisabled: boolean;
}

export const EditQuestionCard = ({
  question,
  formId,
  isDisabled,
  ...cardProps
}: EditQuestionCardProps) => {
  const { ...fragments } = useFragment(fragment, question);
  const [updateQuestion, isUpdateLoading] =
    useMutation<EditQuestionCard_updateQuestion_Mutation>(updateMutation);

  const saveQuestion = useCallback(
    debounce((input: FormsUpdateQuestionMutationInput) => {
      updateQuestion({
        variables: {
          input,
        },
      });
    }, 350),
    [],
  );

  return (
    <DraggableCard {...cardProps} isNotClickable data-qa="edit-question-card">
      <CardContent>
        <Stack spacing={6}>
          <QuestionTitle
            question={fragments}
            isDisabled={isDisabled}
            saveQuestion={saveQuestion}
          />
          <QuestionInputs
            question={fragments}
            isDisabled={isDisabled}
            saveQuestion={saveQuestion}
          />
          <QuestionControls
            question={fragments}
            formId={formId}
            isDisabled={isDisabled}
            isUpdating={isUpdateLoading}
            saveQuestion={saveQuestion}
          />
        </Stack>
      </CardContent>
    </DraggableCard>
  );
};
